import React from "react";
import { Link } from "react-router-dom";
import "./ExampleBookingPage.css";
import ProfessionalProfile from "../customer/ProfessionalProfile";
import { ArrowRightIcon } from "../public/icons";

// A static, illustrative booking page. Every real booking page is generated
// from a professional's own profile, services and availability; this one
// exists so visitors can see the shape of it before they sign up.
//
// It renders the real ProfessionalProfile with demo data rather than markup of
// its own, so the two cannot drift apart: whatever changes a customer's view of
// a booking page changes this page in the same commit. The demo data below is
// the only thing this file owns.
//
// AppRoutes keeps /example outside PublicSiteShell for the same reason real
// profiles sit outside it — see the header comment in styles/public-site.css.

// Inline SVG keeps the demo entirely self-contained: the page issues no
// network request of any kind, not even for a portfolio image.
const portfolioTile = (background, foreground, label) =>
  `data:image/svg+xml,${encodeURIComponent(
    [
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300">',
      `<rect width="400" height="300" fill="${background}"/>`,
      `<text x="200" y="157" text-anchor="middle" fill="${foreground}"`,
      ' font-family="system-ui, sans-serif" font-size="19">',
      label,
      "</text></svg>",
    ].join("")
  )}`;

const DEMO_PROFESSIONAL = {
  _id: "demo-professional",
  username: "example",
  profileFormat: "standard",
  name: "Nia Adeyemi",
  businessName: "Nia Adeyemi",
  tagline: "Braids, locs and protective styling",
  industryCategory: "Hair / Barbering",
  accountStatus: "active",
  cancellationPolicy: "moderate",
  location: { city: "Brooklyn", state: "NY", zip: "11216" },
  bio:
    "Twelve years of hands in hair. I take my time — most appointments run long " +
    "on purpose, because rushing an install is how you end up with tension. Come " +
    "with your hair washed and detangled, and bring something to watch.",
  // Shown as a pro who takes cards, so the deposits below read the way they do
  // on a real page: part today, the rest on the day. A cash-only page collects
  // nothing online and would drop them. Booking is off here, so no payment is
  // ever started. Set the way the public profile payload sends it.
  paymentMode: "card",
  gallery: [
    {
      id: "demo-gallery-1",
      url: portfolioTile("#ebddc5", "#82796a", "Your work, your photos"),
      alt: "Placeholder for a portfolio photo the professional uploads",
      caption: "Knotless braids, waist length",
    },
    {
      id: "demo-gallery-2",
      url: portfolioTile("#ffe1d0", "#643312", "Portfolio image"),
      alt: "Placeholder for a portfolio photo the professional uploads",
      caption: "Loc retwist, barrel curls",
    },
    {
      id: "demo-gallery-3",
      url: portfolioTile("#e1eecc", "#56633f", "Portfolio image"),
      alt: "Placeholder for a portfolio photo the professional uploads",
      caption: "Silk press on 4c hair",
    },
    {
      id: "demo-gallery-4",
      url: portfolioTile("#eee7db", "#645c50", "Portfolio image"),
      alt: "Placeholder for a portfolio photo the professional uploads",
      caption: "Fulani braids with cuffs",
    },
  ],
};

// A real service arrives with its business's booking settings. This pro has
// paying in person switched on, so clients can pay online or on the day.
const DEMO_BOOKING_SETTINGS = { inPersonPaymentEnabled: true };

const DEMO_SERVICES = [
  {
    _id: "demo-service-braids",
    title: "Knotless braids — medium",
    description: "Hair not included. Message me beforehand if you want colour added.",
    price: 180,
    deposit: 90,
    duration: 330,
    locationType: "at_location",
    travelFee: 0,
    bookingSettings: DEMO_BOOKING_SETTINGS,
  },
  {
    _id: "demo-service-locs",
    title: "Loc retwist & style",
    description: "Retwist, a light steam and the style of your choice.",
    price: 75,
    deposit: 25,
    duration: 150,
    locationType: "both",
    travelFee: 15,
    bookingSettings: DEMO_BOOKING_SETTINGS,
  },
  {
    _id: "demo-service-silk-press",
    title: "Silk press & trim",
    description: "Wash, blow-dry, press and a dusting of the ends.",
    price: 65,
    deposit: 0,
    duration: 90,
    locationType: "at_location",
    travelFee: 0,
    bookingSettings: DEMO_BOOKING_SETTINGS,
  },
];

// Thursday and Sunday are left out so visitors see closed days too.
const DEMO_AVAILABILITY = [
  { day: "Monday", slots: [{ start: "09:00", end: "15:30" }] },
  { day: "Tuesday", slots: [{ start: "09:00", end: "15:30" }] },
  { day: "Wednesday", slots: [{ start: "11:00", end: "17:30" }] },
  { day: "Friday", slots: [{ start: "09:00", end: "15:30" }] },
  { day: "Saturday", slots: [{ start: "10:00", end: "16:30" }] },
];

// The Ember palette the marketing site uses, expressed through the same theme
// fields a professional picks from in the booking page editor.
const DEMO_THEME = {
  // Straight from the Organic design system the public site is built on:
  // --color-bg, --color-surface, --color-neutral-100/700, --color-accent and
  // --color-divider flattened onto the page background.
  backgroundColor: "#f5ead8",
  sectionBackgroundColor: "#ebddc5",
  serviceCardBackgroundColor: "#f9f4ed",
  serviceCardBackgroundCustomized: true,
  textColor: "#201e1d",
  secondaryTextColor: "#645c50",
  accentColor: "#c67139",
  borderColor: "#d3c9ba",
  borderRadius: 28,
  borderStyle: "subtle",
  shadowStyle: "soft",
  spacingDensity: "balanced",
  servicesLayout: "card",
  backgroundShape: "circles",
  backgroundShapeDensity: "subtle",
  backgroundShapeColor: "accent",
  fontFamily: "Poppins",
  servicesHeading: "Book time with Nia",
  primaryCtaText: "Book this",
};

const ExampleBookingPage = () => (
  <>
    <div className="example-banner">
      <div className="example-banner__inner">
        <p>
          <strong>Example booking page.</strong> This is what your clients see. Yours
          carries your name, services, hours and colors.
        </p>
        <Link className="example-banner__cta" to="/signup">
          Create yours
          <ArrowRightIcon size={15} />
        </Link>
      </div>
    </div>

    <ProfessionalProfile
      professionalData={DEMO_PROFESSIONAL}
      servicesData={DEMO_SERVICES}
      availabilityData={DEMO_AVAILABILITY}
      themeOverride={DEMO_THEME}
      disableBooking
      demoCtaHref="/signup"
    />
  </>
);

export default ExampleBookingPage;
